import React, { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import MessageComposer from '../MessageComposer';
import '../ThreadQuill.css';
import PlayerLayout from './PlayerLayout';
import MessageBubble from '../MessageBubble';
import {
  fetchThread,
  fetchMoreMessages,
  sendReply,
  markThreadRead,
  markConversationUnread,
  clearThread,
  fetchUnreadCount,
} from '../store/conversationsSlice';

const normalizeId = value => (value && typeof value === 'object' && value._id ? value._id : value)?.toString?.() || '';

const PlayerThreadPage = ({ match }) => {
  const { conversationId } = match.params;
  const dispatch = useDispatch();
  const history = useHistory();
  const { activeThread, messages, nextCursor, loading } = useSelector(
    ({ conversations: convState }) => convState
  );
  const bottomRef = useRef(null);
  const initialScrollDone = useRef(false);

  useEffect(() => {
    dispatch(fetchThread(conversationId));
    dispatch(markThreadRead(conversationId)).then(() => dispatch(fetchUnreadCount()));
    return () => dispatch(clearThread());
  }, [dispatch, conversationId]);

  const scrollToBottom = useCallback((behavior) => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: behavior || 'auto', block: 'end' });
    }
  }, []);

  useEffect(() => {
    if (!initialScrollDone.current && messages.length > 0) {
      scrollToBottom('auto');
      initialScrollDone.current = true;
    }
  }, [messages, scrollToBottom]);

  const handleLoadMore = () => {
    if (nextCursor) dispatch(fetchMoreMessages(conversationId, nextCursor));
  };

  const coach = activeThread?.coachUserId;
  const coachName = coach ? `${coach.firstName} ${coach.lastName}` : 'Coach';
  const coachWithSchool = coach?.schoolName
    ? `${coachName} - <${coach.schoolName}>`
    : coachName;
  const isAssistantConversation = coach?.role === 'assistant';
  const isAdminConversation = coach?.role === 'admin';
  const threadDisplayName = isAssistantConversation ? 'agencja' : (isAdminConversation ? 'Kajetan Borecki' : coachWithSchool);

  if (loading && !activeThread) {
    return (
      <PlayerLayout>
        <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
          Loading conversation...
        </div>
      </PlayerLayout>
    );
  }

  return (
    <PlayerLayout>
      <div style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <button
            type="button"
            onClick={() => history.push('/player/inbox')}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#2e496b',
              fontSize: '14px',
              padding: 0,
            }}
          >
            &#8592; Back to Inbox
          </button>
          <button
            type="button"
            onClick={async () => {
              await dispatch(markConversationUnread(conversationId));
              history.push('/player/inbox');
            }}
            style={{
              background: 'none',
              border: '1px solid #cbd5e1',
              borderRadius: '4px',
              cursor: 'pointer',
              color: '#64748b',
              fontSize: '12px',
              padding: '4px 10px',
              marginLeft: '16px',
            }}
          >
            Oznacz jako nieprzeczytane
          </button>
        </div>
        <h2 style={{ margin: '8px 0 0', fontSize: '18px', color: '#1e293b' }}>
          {threadDisplayName}
        </h2>
      </div>

      <div
        style={{
          background: '#ffffff',
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          overflow: 'hidden',
        }}
      >
        {/* Message list */}
        <div style={{ padding: '0' }}>
          {nextCursor && (
            <div style={{ textAlign: 'center', padding: '12px' }}>
              <button
                type="button"
                onClick={handleLoadMore}
                style={{
                  background: 'none',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  padding: '4px 12px',
                  fontSize: '12px',
                  color: '#64748b',
                  cursor: 'pointer',
                }}
              >
                Load earlier messages
              </button>
            </div>
          )}
          {messages.map(msg => {
            const coachUserId = normalizeId(activeThread?.coachUserId);
            const senderUserId = normalizeId(msg.senderUserId);
            const isAssistantMessage = msg.senderType === 'coach' && senderUserId && coachUserId && senderUserId !== coachUserId;
            const isPrimaryCounterpartyMessage = msg.senderType === 'coach' && senderUserId && coachUserId && senderUserId === coachUserId;
            let senderLabel;
            if (msg.senderType === 'player') senderLabel = 'You';
            else if (msg.senderType === 'admin') senderLabel = 'Kajetan Borecki';
            else if (isAssistantMessage) senderLabel = 'agencja';
            else if (isPrimaryCounterpartyMessage && isAssistantConversation) senderLabel = 'agencja';
            else if (isPrimaryCounterpartyMessage && isAdminConversation) senderLabel = 'Kajetan Borecki';
            else senderLabel = threadDisplayName;

            return (
              <MessageBubble
                key={msg._id}
                message={msg}
                isSentByMe={msg.senderType === 'player'}
                senderLabel={senderLabel}
                senderType={msg.senderType}
              />
            );
          })}
          <div ref={bottomRef} />
        </div>

        {/* Reply input */}
        <MessageComposer
          threadType="conversations"
          conversationId={conversationId}
          placeholder="Type a reply..."
          onSend={({ text, file, draftRevision }) => dispatch(sendReply({ conversationId, text, file, draftRevision }))}
          onSent={() => { setTimeout(() => scrollToBottom('smooth'), 50); }}
        />
      </div>
    </PlayerLayout>
  );
};

export default PlayerThreadPage;
